"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Briefcase, MapPin, Clock, CircleCheck, ArrowRight, ShieldCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const responsibilities = ["Design, implement, and maintain robust security architectures to protect critical infrastructure.", "Monitor network traffic for security incidents, anomalies, and potential threats.", "Configure, manage, and troubleshoot security infrastructure devices including firewalls and VPNs.", "Develop, implement, and enforce comprehensive security policies and procedures.", "Perform regular vulnerability testing, risk analyses, and security assessments."];
const requirements = ["Proven experience working as a Network Security Engineer or in a similar cybersecurity role.", "Deep technical understanding of firewalls, intrusion detection/prevention systems, and VPNs.", "Strong familiarity with network protocols, routing, and switching technologies.", "Relevant industry certifications (e.g., CISSP, CCNP Security, CEH, or CISM).", "Excellent problem-solving skills and the ability to work under pressure during security incidents."];
export default function JobOpening() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const cardReveal = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <section id="job-opening" className="py-16 md:py-24 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        {}
        <div ref={headerReveal.ref} className={`text-center mb-12 transition-all duration-700 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-4">
            <ShieldCheck className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold font-default mb-4">
            Join the SecureNetics Team
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            At SecureNetics, we are always searching for highly skilled and
            passionate professionals to help us secure the digital frontier.
            Discover your next career opportunity with us.
          </p>
        </div>

        {}
        <div ref={cardReveal.ref} className={`transition-all duration-700 delay-200 ease-out ${cardReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Card className="border-border shadow-lg shadow-shadow-sm overflow-hidden">
            <div className="h-2 w-full bg-primary" />
            <CardHeader className="pb-6 md:pb-8">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                <div>
                  <CardTitle className="text-2xl md:text-3xl font-bold font-default mb-2">
                    Network Security Engineer
                  </CardTitle>
                  <CardDescription className="text-base">
                    SecureNetics • Engineering Department
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="default" className="bg-primary/10 text-primary hover:bg-primary/20 border-transparent">
                    <Briefcase className="w-3 h-3 mr-1" />
                    Full-time
                  </Badge>
                  <Badge variant="secondary">
                    <MapPin className="w-3 h-3 mr-1" />
                    Remote / Hybrid
                  </Badge>
                  <Badge variant="outline" className="border-border">
                    <Clock className="w-3 h-3 mr-1" />
                    Immediate Start
                  </Badge>
                </div>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                We are actively seeking a talented Network Security Engineer to
                join our elite team. In this critical role, you will be at the
                forefront of defending our clients' networks against sophisticated
                cyber threats. If you have a deep understanding of network
                security principles and a proactive approach to threat mitigation,
                we want you on our team.
              </p>
            </CardHeader>

            <Separator />

            <CardContent className="py-6 md:py-8 grid md:grid-cols-2 gap-8">
              {}
              <div>
                <h3 className="text-lg font-semibold font-default mb-4 flex items-center text-foreground">
                  Key Responsibilities
                </h3>
                <ul className="space-y-3">
                  {responsibilities.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                      <CircleCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>

              {}
              <div>
                <h3 className="text-lg font-semibold font-default mb-4 flex items-center text-foreground">
                  Requirements
                </h3>
                <ul className="space-y-3">
                  {requirements.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                      <CircleCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>
            </CardContent>

            <Separator />

            <CardFooter className="py-6 bg-muted/30 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                <p>SecureNetics is an equal opportunity employer.</p>
                <p>Always searching for top-tier talent.</p>
              </div>
              <Button asChild size="lg" className="w-full sm:w-auto group">
                <Link to="#apply">
                  Apply for this position
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </section>;
}